import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_BASE, getToken } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
    fetch(`${API_BASE}/auth/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.data.role === 'vendor') {
          setUser(data.data);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('vendorToken');
        }
      })
      .catch(() => localStorage.removeItem('vendorToken'))
      .finally(() => setIsLoading(false));
  }, []);

  const setSession = (token, userData) => {
    localStorage.setItem('vendorToken', token);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const login = async (email, password) => {
    const res = await fetch(`${API_BASE}/auth/vendor/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Login failed');
    setSession(data.data.token, data.data.user);
    toast.success('Welcome back');
    return data;
  };

  const logout = () => {
    localStorage.removeItem('vendorToken');
    setUser(null);
    setIsAuthenticated(false);
  };

  const isApproved = user?.onboarding_status === 'approved' || !user?.onboarding_status;
  const needsOnboarding = isAuthenticated && !isApproved;

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, user, setSession, login, logout, isApproved, needsOnboarding }}
    >
      {children}
    </AuthContext.Provider>
  );
};
